import type { SpringValues } from '@react-spring/web';
import { animated, useTransition } from '@react-spring/web';
import { useState } from 'react';
import { Box } from '@mentimeter/ragnar-ui/box';
import type { ClickableT } from '@mentimeter/ragnar-ui/clickable';
import { Clickable } from '@mentimeter/ragnar-ui/clickable';
import { Text } from '@mentimeter/ragnar-ui/text';
import { useTranslations } from 'next-intl';
import { usePresentationContext } from '../../../../legacy-context/presentationContext';
import type { OpenEndedResponse } from '../types';

const ICON_SPACING = 15;

const AnimatedClickable = ({
  readonly,
  enableAnimations,
  ariaLabel,
  onClick,
  children,
  ...props
}: {
  readonly: boolean;
  enableAnimations: boolean;
  ariaLabel: string;
  id: string | undefined;
  onClick: () => void;
  children: React.ReactNode;
}) => {
  const transitions = useTransition(props.id, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    config: {
      duration: 200,
    },
  });

  if (readonly) return <Box {...props}>{children}</Box>;

  return transitions((styles) => (
    <Clickable
      as={animated.button}
      aria-label={ariaLabel}
      onClick={onClick}
      extend={({ theme }) => ({
        transition: `background, color ${enableAnimations ? 0.5 : 0}s ease`,
      })}
      style={styles as SpringValues}
      {...props}
    >
      {children}
    </Clickable>
  ));
};

interface ResponseProps extends Omit<ClickableT, 'onClick'> {
  responseText: string;
  index: number;
  participantDisplayName?: OpenEndedResponse['participantDisplayName'];
  onClick: (index: number) => void;
  answerFontSize?: string | undefined;
  controls?: React.ReactNode;
  controlsOnHover?: React.ReactNode;
}

export const Response = ({
  responseText,
  index,
  onClick,
  answerFontSize,
  controls,
  controlsOnHover,
  participantDisplayName,
  ...props
}: ResponseProps) => {
  const [shouldShowControlsOnHover, setShouldShowControlsOnHover] =
    useState(false);
  const { useConfig } = usePresentationContext();

  const { enableAnimations, readonly } = useConfig();
  const translate = useTranslations('presenting.legacy');

  return (
    <Box
      width="100%"
      onMouseEnter={() => setShouldShowControlsOnHover(true)}
      onMouseLeave={() => setShouldShowControlsOnHover(false)}
    >
      <AnimatedClickable
        readonly={readonly}
        enableAnimations={enableAnimations}
        ariaLabel={translate('actions.show_single_question')}
        id={`flow-item-${index}`}
        onClick={() => onClick(index)}
        width="100%"
        p="space10"
        borderRadius="3xl"
        bg={shouldShowControlsOnHover ? 'neutral' : 'neutralWeak'}
        color="text"
        {...props}
      >
        <Text
          textAlign="start"
          fontSize={answerFontSize ?? '175'}
          lineHeight="snug"
          color="inherit"
        >
          {responseText}
        </Text>
        {participantDisplayName && (
          <Text fontSize="125" mt="space3" color="textWeak">
            {participantDisplayName}
          </Text>
        )}
      </AnimatedClickable>
      {controls && !readonly && (
        <Box
          position="absolute"
          height={`calc(100% - ${ICON_SPACING * 2}px)`}
          justifyContent="space-between"
          style={{
            top: `${ICON_SPACING}px`,
            right: `${ICON_SPACING}px`,
          }}
        >
          {controls}
        </Box>
      )}
      {controlsOnHover && !readonly && (
        <Box
          position="absolute"
          justifyContent="space-between"
          color="onNeutralWeak"
          style={{
            top: `${ICON_SPACING}px`,
            right: `${ICON_SPACING}px`,
            opacity: shouldShowControlsOnHover ? 1 : 0,
            transition: 'opacity 0.5s ease',
          }}
        >
          {controlsOnHover}
        </Box>
      )}
    </Box>
  );
};
